import { useCurrentCommunity } from 'components';
import {
	Box,
	Grid,
	Typography,
	Heading,
	Paper,
	LoadingButton,
	PickerFile,
} from 'components/common';
import {
	SetupCommunityProfileForm,
	SetupCommunityProfileSubmittedValues,
	SetupCommunityStepper,
} from 'components/community/setup';
import {
	COMMUNITY_SETUP_MEMBERS,
	COMMUNITY_SETUP_COMPLETED,
} from 'core/constants';
import { useCurrentIdentity } from 'core/uses-cases/authentication';
import { usePatchCommunity } from 'core/uses-cases/community';
import { usePatchIdentity } from 'core/uses-cases/identity';
import { useRouter, useServices, useTranslation } from 'hooks';
import { useCallback } from 'react';
import { Navigate } from 'react-router-dom';

export const SetupProfileCommunity = () => {
	const { t } = useTranslation();
	const community = useCurrentCommunity();
	const identity = useCurrentIdentity();
	const router = useRouter();
	const { notification } = useServices();

	const communityMutation = usePatchCommunity();
	const identityMutation = usePatchIdentity();

	const handleOnSubmit = useCallback(
		async (values: SetupCommunityProfileSubmittedValues) => {
			if (!community || !identity) return undefined;

			const oldSlug = community.slug;
			const newSlug = values.community.slug || oldSlug;

			await communityMutation.mutateAsync({
				communityId: oldSlug,
				name: community.name,
				slug: newSlug,
				image: values.community.image,
				setup_status: COMMUNITY_SETUP_MEMBERS,
			});

			await identityMutation.mutateAsync({
				identityId: identity.uuid,
				firstName: identity.first_name,
				lastName: identity.last_name,
				email: identity.email,
				image: values.identity.image,
				detectTimezone: identity.autodetect_timezone,
			});

			notification.show(
				'success',
				t('edit-community.community-update-success')
			);

			router.navigate('community.setup-members', {
				communityId: newSlug,
			});
		},
		[community, identity]
	);

	const handleOnSkipToHomepage = useCallback(() => {
		if (!community) return;

		communityMutation.mutate(
			{
				name: community.name,
				communityId: community.slug,
				setup_status: COMMUNITY_SETUP_COMPLETED,
			},
			{
				onSuccess: () => {
					router.navigate('community.home', {
						communityId: community.slug,
					});
				},
			}
		);
	}, [community]);

	const handleOnGoToStep2 = useCallback(() => {
		if (!community) return;

		communityMutation.mutate(
			{
				name: community.name,
				communityId: community.slug,
				setup_status: COMMUNITY_SETUP_MEMBERS,
			},
			{
				onSuccess: () => {
					router.navigate('community.setup-members', {
						communityId: community.slug,
					});
				},
			}
		);
	}, [community]);

	if (!community) {
		return <Navigate to='/?error=community-not-found' replace />;
	}

	if (!identity) {
		return <Navigate to='/?error=identity-not-found' replace />;
	}

	return (
		<Box>
			<Grid mb={4} container spacing={3}>
				<Grid size={{ xs: 12, md: 8 }}>
					<Heading variant='h4' mb={1}>
						{t('create-community.create-community')}
					</Heading>
					<Typography variant='body2' mb={1}>
						{t("create-community.you're-almost-there")}
					</Typography>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<Box
						display='flex'
						justifyContent='flex-end'
						alignItems='center'
						height='100%'
						gap={2}
					>
						<LoadingButton
							onClick={handleOnSkipToHomepage}
							variant='outlined'
							size='small'
							loading={communityMutation.isPending}
							loadingIndicator={t('community-form.going-home')}
							data-test-id='setup-community-skip-to-homepage-step-1'
						>
							{t('community-form.skip-to-homepage')}
						</LoadingButton>
						<LoadingButton
							onClick={handleOnGoToStep2}
							size='small'
							loading={communityMutation.isPending}
							loadingIndicator={t(
								'community-form.going-to-step-2'
							)}
							data-test-id='setup-community-next-step-1'
						>
							{t('community-form.step-2')}:{' '}
							{t('community-form.add-members')}
						</LoadingButton>
					</Box>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<SetupCommunityStepper />
				</Grid>
			</Grid>
			<Box component={Paper} elevation={0} px={5} py={5}>
				<SetupCommunityProfileForm
					onSubmit={handleOnSubmit}
					community={community}
					identity={identity}
					initialValues={{
						communitySlug: community.slug,
						communityImage: community.image
							? ([community.image] as PickerFile[])
							: undefined,
						identityImage: identity.image
							? ([identity.image] as PickerFile[])
							: undefined,
					}}
				/>
			</Box>
		</Box>
	);
};
